import { useState } from 'preact/hooks';
import { useLocation } from 'preact-iso';
import { api } from '../../lib/api.js';
import { useApi } from '../../hooks/useApi.js';
import { useAuth } from '../../hooks/useAuth.jsx';
import { Button } from '../../components/Button.jsx';
import { CHANNELS } from '../../lib/channels.js';
import { MixForm } from './MixForm.jsx';

const DIRECTION_LABEL = {
  surface: 'Surface',
  reverse: 'Reverse',
  cbw_surface: 'CBW surface',
};

const MixCard = ({ mix, isAdmin, onEdit, onDelete }) => (
  <div class="surface-card p-4">
    <div class="mb-3 flex items-baseline justify-between gap-3">
      <div>
        <p class="m-0 font-serif text-[16px] font-medium text-text-primary">
          {mix.printerName}
        </p>
        <p class="m-0 text-[12px] font-medium text-text-tertiary">
          {DIRECTION_LABEL[mix.printDirection] ?? mix.printDirection}
        </p>
      </div>
      <div class="flex items-center gap-2">
        {mix.deltaE != null && (
          <span class="font-serif text-sm font-medium text-text-secondary">
            ΔE {mix.deltaE.toFixed(1)}
          </span>
        )}
        {isAdmin && (
          <>
            <button
              onClick={() => onEdit(mix)}
              class="rounded border border-border-strong px-2 py-0.5 text-[11px] font-semibold text-text-tertiary hover:text-text-primary"
            >
              Edit
            </button>
            <button
              onClick={() => onDelete(mix)}
              class="rounded border border-border-strong px-2 py-0.5 text-[11px] font-semibold text-text-tertiary hover:text-ink-magenta"
            >
              Delete
            </button>
          </>
        )}
      </div>
    </div>
    <div class="grid grid-cols-4 gap-1.5 sm:grid-cols-8">
      {Object.entries(mix.recipe).map(([code, value]) => {
        const ch = CHANNELS[code];
        if (!ch) return null;
        return (
          <div key={code} class="rounded-md border border-border-subtle bg-bg-base p-2 text-center">
            <p class="m-0 text-[10px] font-bold" style={{ color: ch.color }}>
              {code.toUpperCase()}
            </p>
            <p class="m-0 font-serif text-[18px] font-medium text-text-primary">
              {value}
            </p>
          </div>
        );
      })}
    </div>
  </div>
);

export const InkDetailPage = ({ bookId, inkId }) => {
  const { route } = useLocation();
  const { data: ink, loading, error, refetch } = useApi(`/ink-books/inks/${inkId}`);
  const { data: printers } = useApi('/printers');
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';

  const [mixFormOpen, setMixFormOpen] = useState(false);
  const [editingMix, setEditingMix] = useState(null);
  const [busy, setBusy] = useState(false);

  const handleDeleteInk = async () => {
    if (!window.confirm(`Delete "${ink.name}"? All saved mixes will also be deleted.`)) return;
    setBusy(true);
    try {
      await api.delete(`/ink-books/inks/${ink.id}`);
      route(`/ink-books/${bookId}`);
    } catch (err) {
      alert(err.message ?? 'Failed to delete');
      setBusy(false);
    }
  };

  const handleDeleteMix = async (mix) => {
    if (!window.confirm(`Delete the ${mix.printerName} · ${DIRECTION_LABEL[mix.printDirection]} mix?`)) return;
    try {
      await api.delete(`/ink-books/mixes/${mix.id}`);
      refetch();
    } catch (err) {
      alert(err.message ?? 'Failed to delete');
    }
  };

  return (
    <div>
      <a href={`/ink-books/${bookId}`} class="mb-3 inline-block text-[12px] font-semibold uppercase tracking-eyebrow text-text-tertiary hover:text-text-primary">
        ← Book
      </a>

      {loading && <p class="text-sm text-text-tertiary">Loading…</p>}
      {error && (
        <p class="text-sm font-semibold text-ink-magenta">
          Failed to load: {error.message}
        </p>
      )}

      {ink && (
        <>
          <section class="mb-8 flex items-start justify-between gap-4">
            <div class="flex items-start gap-4">
              <span
                class={`block h-16 w-16 shrink-0 rounded-lg border ${ink.targetHex ? 'border-border-subtle' : 'border-dashed border-border-strong'}`}
                style={ink.targetHex ? { background: ink.targetHex } : undefined}
                aria-hidden="true"
              />
              <div>
                <p class="eyebrow mb-1.5" style={{ color: '#00B4D8' }}>Ink</p>
                <h1 class="m-0 mb-1.5 text-[30px] leading-[1.18] tracking-tight text-text-primary">
                  {ink.name}
                </h1>
                <p class="m-0 text-sm text-text-secondary">
                  {ink.targetHex && (
                    <span class="font-mono">{ink.targetHex}</span>
                  )}
                  {ink.targetHex && ink.pantoneCode && <span class="text-text-muted"> · </span>}
                  {ink.pantoneCode && (
                    <>Pantone code <span class="font-mono">{ink.pantoneCode}</span></>
                  )}
                </p>
              </div>
            </div>
            <div class="flex shrink-0 items-center gap-2">
              <a
                href={`/converter?inkId=${ink.id}`}
                class="rounded-md border border-border-strong bg-transparent px-4 py-2 text-sm font-medium text-text-body hover:bg-bg-card focus:outline-none focus-visible:ring-2 focus-visible:ring-brand"
              >
                Open in Converter ↗
              </a>
              {isAdmin && (
                <Button variant="ghost" onClick={handleDeleteInk} disabled={busy} class="text-ink-magenta hover:text-ink-magenta">
                  Delete ink
                </Button>
              )}
            </div>
          </section>

          <section class="mb-8">
            <div class="mb-3 flex items-center justify-between">
              <p class="label m-0">Target LAB</p>
              <button
                type="button"
                disabled
                title="X-Rite integration coming once DataCatcher is wired up — see XRITE.md"
                class="inline-flex cursor-not-allowed items-center gap-2 rounded-md border border-border-strong bg-transparent px-3 py-1.5 text-[12px] font-semibold text-text-tertiary opacity-60"
              >
                <span class="block h-3.5 w-3.5 rounded-full border-[1.5px] border-current">
                  <span class="block h-full w-full scale-50 rounded-full bg-current" />
                </span>
                Scan with X-Rite
                <span class="text-[10px] font-medium text-text-muted">· coming</span>
              </button>
            </div>
            {ink.targetLab ? (
              <div class="grid max-w-md grid-cols-3 gap-2">
                {[
                  { label: 'L', value: ink.targetLab.l },
                  { label: 'a', value: ink.targetLab.a },
                  { label: 'b', value: ink.targetLab.b },
                ].map(({ label, value }) => (
                  <div key={label} class="surface-card p-4 text-center">
                    <p class="m-0 text-[11px] font-semibold uppercase tracking-eyebrow text-text-tertiary">
                      {label}
                    </p>
                    <p class="m-0 mt-1 font-serif text-[28px] font-medium tracking-tighter text-text-primary">
                      {value.toFixed(1)}
                    </p>
                  </div>
                ))}
              </div>
            ) : (
              <div class="surface-card max-w-md p-4">
                <p class="m-0 text-sm font-semibold text-brand">Not yet measured.</p>
                <p class="m-0 mt-1 text-[13px] font-medium text-text-tertiary">
                  Scan this ink with the X-Rite (or use the Converter) and save to fill in the target LAB.
                </p>
              </div>
            )}
          </section>

          <section>
            <div class="mb-3 flex items-center justify-between">
              <p class="label m-0">
                Mixes · {ink.mixes.length}
              </p>
              {isAdmin && !mixFormOpen && (
                <Button variant="secondary" onClick={() => { setEditingMix(null); setMixFormOpen(true); }}>
                  + Add mix
                </Button>
              )}
            </div>

            {mixFormOpen && printers && (
              <div class="mb-4">
                <MixForm
                  inkId={ink.id}
                  printers={printers}
                  existingMix={editingMix}
                  onSaved={() => { setMixFormOpen(false); setEditingMix(null); refetch(); }}
                  onCancel={() => { setMixFormOpen(false); setEditingMix(null); }}
                />
              </div>
            )}

            {ink.mixes.length === 0 && !mixFormOpen ? (
              <div class="surface-card p-4">
                <p class="m-0 text-sm font-semibold text-text-secondary">No saved mixes yet.</p>
                <p class="m-0 mt-1 text-[13px] font-medium text-text-tertiary">
                  {isAdmin
                    ? 'Click "+ Add mix" to record a press recipe for a printer + direction.'
                    : 'Star a winning match in the Converter (Compare mode, coming with X-Rite).'}
                </p>
              </div>
            ) : (
              <div class="grid grid-cols-1 gap-3 lg:grid-cols-2">
                {ink.mixes.map((mix) => (
                  <MixCard
                    key={mix.id}
                    mix={mix}
                    isAdmin={isAdmin}
                    onEdit={(m) => { setEditingMix(m); setMixFormOpen(true); }}
                    onDelete={handleDeleteMix}
                  />
                ))}
              </div>
            )}
          </section>
        </>
      )}
    </div>
  );
};
